import type { CaseItem, CaseResponse, TargetType } from "../../../packages/core/src/types";
import type { Appearance } from "../../../packages/ui/src";

const SOURCE = "https://www.safe182.go.kr/home/lcm/lcmMssList.do?rptDscd=2";
const SNAPSHOT_MAX_AGE_MS = 2 * 60 * 60 * 1000;

export function resolveApiUrl(path: string, base = import.meta.env.VITE_API_BASE_URL): string {
  if (!path.startsWith("/api/")) throw new Error("api_path_must_be_relative");
  if (!base) return path;
  const origin = new URL(base);
  if (origin.protocol !== "https:" || origin.pathname !== "/" || origin.search || origin.hash) throw new Error("invalid_api_base_url");
  return new URL(path, origin).toString();
}

type ApiLocation = { raw: string; sigunguCode?: string | null; precision: CaseItem["precision"]; lon: number | null; lat: number | null; precisionLabel: CaseItem["precisionLabel"] };
type ApiAppearance = Record<string, string | number | null>;
type ApiMissing = { caseId: string; nameMasked: string; sex: string | null; ageAtMissing: number | null; ageNow: number | null; targetType: "child" | "disabled" | "dementia" | "runaway" | "other"; occurredAt: string; firstSeenAt?: string | null; location: ApiLocation; appearance: ApiAppearance; photoUrl?: string | null };
type ApiProtected = { protectedId: string; sex: string | null; ageEstimated: number | null; foundAt: string; location: ApiLocation; appearance: ApiAppearance; facilityContact?: string | null };
type ApiList<T> = { meta: { checkedAt?: string | null; total: number; nextCursor?: string | null }; items: T[] };

const targetLabels: Record<ApiMissing["targetType"], TargetType> = {
  child: "아동",
  disabled: "지적·자폐성·정신장애인",
  dementia: "치매환자",
  runaway: "성인",
  other: "성인",
};

const appearanceLabels: Record<string, string> = { heightCm: "키", weightKg: "몸무게", build: "체격", faceShape: "얼굴형", hairShape: "두발형태", hairColor: "머리색", clothing: "옷차림" };

export type CaseDetailView = { item: CaseItem; appearance: Appearance; photoUrl: string | null; checkedAt: string | null };
export type ProtectedDetailView = { item: CaseItem; appearance: Appearance; facilityContact: string | null; checkedAt: string | null };
export type MatchView = { candidates: Array<{ item: CaseItem; reasons: string[] }>; disclaimer: string; checkedAt: string | null };
export type ReturnedItem = { entityType: "missing" | "protected"; entityId: string; nameMasked: string | null; withdrawnAt: string };
export type StatusView = { protectedSync?: { healthy: boolean; consecutiveFailures: number | null; lastCheckedAt: string | null }; lastCheckedAt: string | null; nextCheckAt: string; healthy: boolean; consecutiveFailures: number | null; counts: { missingActive: number | null; protectedActive: number | null }; recentWithdrawals: Array<{ entityType: "missing" | "protected"; nameMasked: string | null; withdrawnAt: string }>; apiCallsToday: number | null; apiCallCap: number };

export class ApiClientError extends Error {
  constructor(public readonly status: number, public readonly code: string, public readonly withdrawnAt: string | null = null) {
    super(code);
  }
}

function appearanceLines(value: ApiAppearance): string[] {
  return Object.entries(value).flatMap(([key, item]) => item === null || item === "" ? [] : [`${appearanceLabels[key] ?? key}: ${item}${key === "heightCm" ? "cm" : key === "weightKg" ? "kg" : ""}`]);
}

function adaptMissingItem(item: ApiMissing): CaseItem {
  return { id: item.caseId, name: item.nameMasked, sex: item.sex ?? "미상", ageAtMissing: item.ageAtMissing, ageNow: item.ageNow, targetType: targetLabels[item.targetType], occurredAt: item.occurredAt, publishedAt: item.firstSeenAt ?? null, address: item.location.raw, appearance: appearanceLines(item.appearance), lon: item.location.lon, lat: item.location.lat, precision: item.location.precision, precisionLabel: item.location.precisionLabel, sigunguCode: item.location.sigunguCode ?? null };
}

export function adaptMissingResponse(payload: ApiList<ApiMissing>): CaseResponse {
  return {
    items: payload.items.map(adaptMissingItem),
    total: payload.meta.total,
    checkedAt: payload.meta.checkedAt ?? null,
    source: SOURCE,
    sourceStatus: "live",
  };
}

export async function fetchCaseDetail(caseId: string, fetcher: typeof fetch = fetch): Promise<CaseDetailView> {
  const response = await fetcher(resolveApiUrl(`/api/v1/cases/${encodeURIComponent(caseId)}`), { headers: { accept: "application/json" }, cache: "no-store" });
  const payload = await response.json() as { item?: ApiMissing; checkedAt?: string | null; code?: string; withdrawnAt?: string | null };
  if (!response.ok || !payload.item) throw new ApiClientError(response.status, payload.code ?? "AB-5001", payload.withdrawnAt ?? null);
  return { item: adaptMissingItem(payload.item), appearance: payload.item.appearance as Appearance, photoUrl: payload.item.photoUrl ?? null, checkedAt: payload.checkedAt ?? null };
}

export function writeCaseDetailSnapshot(storage: Storage, detail: CaseDetailView, now = Date.now()): void {
  storage.setItem(`arabom:detail-snapshot:${detail.item.id}`, JSON.stringify({ savedAt: now, detail }));
}

export function readCaseDetailSnapshot(storage: Storage, caseId: string, now = Date.now()): CaseDetailView | null {
  try {
    const value = JSON.parse(storage.getItem(`arabom:detail-snapshot:${caseId}`) ?? "null") as { savedAt?: unknown; detail?: unknown } | null;
    if (!value || typeof value.savedAt !== "number" || now - value.savedAt > SNAPSHOT_MAX_AGE_MS || !value.detail) return null;
    return value.detail as CaseDetailView;
  } catch {
    return null;
  }
}

export function adaptProtectedResponse(payload: ApiList<ApiProtected>): CaseResponse {
  return {
    items: payload.items.map((item) => ({ id: item.protectedId, name: "신원 미확인", sex: item.sex ?? "미상", ageAtMissing: item.ageEstimated, ageNow: item.ageEstimated, targetType: "성인", occurredAt: item.foundAt, address: item.location.raw, appearance: appearanceLines(item.appearance), lon: item.location.lon, lat: item.location.lat, precision: item.location.precision, precisionLabel: item.location.precisionLabel, sigunguCode: item.location.sigunguCode ?? null })),
    total: payload.meta.total,
    checkedAt: payload.meta.checkedAt ?? null,
    source: SOURCE,
    sourceStatus: "live",
  };
}

function adaptProtectedItem(item: ApiProtected): CaseItem {
  return { id: item.protectedId, name: "신원 미확인", sex: item.sex ?? "미상", ageAtMissing: item.ageEstimated, ageNow: item.ageEstimated, targetType: "성인", occurredAt: item.foundAt, address: item.location.raw, appearance: appearanceLines(item.appearance), lon: item.location.lon, lat: item.location.lat, precision: item.location.precision, precisionLabel: item.location.precisionLabel, sigunguCode: item.location.sigunguCode ?? null };
}

export async function fetchProtectedDetail(protectedId: string, fetcher: typeof fetch = fetch): Promise<ProtectedDetailView> {
  const response = await fetcher(resolveApiUrl(`/api/v1/protected/${encodeURIComponent(protectedId)}`), { headers: { accept: "application/json" }, cache: "no-store" });
  const payload = await response.json() as { item?: ApiProtected; checkedAt?: string | null; code?: string; withdrawnAt?: string | null };
  if (!response.ok || !payload.item) throw new ApiClientError(response.status, payload.code ?? "AB-5001", payload.withdrawnAt ?? null);
  return { item: adaptProtectedItem(payload.item), appearance: payload.item.appearance as Appearance, facilityContact: payload.item.facilityContact ?? null, checkedAt: payload.checkedAt ?? null };
}

export function writeProtectedDetailSnapshot(storage: Storage, detail: ProtectedDetailView, now = Date.now()): void {
  storage.setItem(`arabom:protected-detail-snapshot:${detail.item.id}`, JSON.stringify({ savedAt: now, detail }));
}

export function readProtectedDetailSnapshot(storage: Storage, protectedId: string, now = Date.now()): ProtectedDetailView | null {
  try {
    const value = JSON.parse(storage.getItem(`arabom:protected-detail-snapshot:${protectedId}`) ?? "null") as { savedAt?: unknown; detail?: unknown } | null;
    if (!value || typeof value.savedAt !== "number" || now - value.savedAt > SNAPSHOT_MAX_AGE_MS || !value.detail) return null;
    return value.detail as ProtectedDetailView;
  } catch {
    return null;
  }
}

export async function fetchMatches(caseId: string, fetcher: typeof fetch = fetch): Promise<MatchView> {
  const response = await fetcher(resolveApiUrl(`/api/v1/cases/${encodeURIComponent(caseId)}/matches?limit=20`), { headers: { accept: "application/json" }, cache: "no-store" });
  const payload = await response.json() as { candidates?: Array<{ item: ApiProtected; reasons: string[] }>; disclaimer?: string; checkedAt?: string | null; code?: string; withdrawnAt?: string | null };
  if (!response.ok) throw new ApiClientError(response.status, payload.code ?? "AB-5001", payload.withdrawnAt ?? null);
  return { candidates: (payload.candidates ?? []).slice(0, 20).map((candidate) => ({ item: adaptProtectedItem(candidate.item), reasons: candidate.reasons.filter((reason) => typeof reason === "string") })), disclaimer: payload.disclaimer ?? "확인은 반드시 182나 해당 기관을 통해 해주세요. 이 목록은 조건이 비슷한 분을 모아 보여드리는 것뿐이에요.", checkedAt: payload.checkedAt ?? null };
}

export function writeMatchSnapshot(storage: Storage, caseId: string, value: MatchView, now = Date.now()): void {
  storage.setItem(`arabom:match-snapshot:${caseId}`, JSON.stringify({ savedAt: now, value }));
}

export function readMatchSnapshot(storage: Storage, caseId: string, now = Date.now()): MatchView | null {
  try {
    const item = JSON.parse(storage.getItem(`arabom:match-snapshot:${caseId}`) ?? "null") as { savedAt?: unknown; value?: unknown } | null;
    if (!item || typeof item.savedAt !== "number" || now - item.savedAt > SNAPSHOT_MAX_AGE_MS || !item.value) return null;
    return item.value as MatchView;
  } catch {
    return null;
  }
}

export async function fetchReturned(fetcher: typeof fetch = fetch): Promise<ReturnedItem[]> {
  const response = await fetcher(resolveApiUrl("/api/v1/returned?limit=100"), { headers: { accept: "application/json" }, cache: "no-store" });
  const payload = await response.json() as { items?: ReturnedItem[]; code?: string };
  if (!response.ok) throw new ApiClientError(response.status, payload.code ?? "AB-5001");
  return (payload.items ?? []).filter((item) => item.entityType === "missing").slice(0, 100);
}

export function writeReturnedSnapshot(storage: Storage, items: ReturnedItem[], now = Date.now()): void {
  storage.setItem("arabom:returned-snapshot", JSON.stringify({ savedAt: now, items }));
}

export function readReturnedSnapshot(storage: Storage, now = Date.now()): ReturnedItem[] | null {
  try {
    const value = JSON.parse(storage.getItem("arabom:returned-snapshot") ?? "null") as { savedAt?: unknown; items?: unknown } | null;
    if (!value || typeof value.savedAt !== "number" || now - value.savedAt > SNAPSHOT_MAX_AGE_MS || !Array.isArray(value.items)) return null;
    return value.items as ReturnedItem[];
  } catch {
    return null;
  }
}

export async function fetchStatus(fetcher: typeof fetch = fetch): Promise<StatusView> {
  const response = await fetcher(resolveApiUrl("/api/v1/status"), { headers: { accept: "application/json" }, cache: "no-store" });
  if (!response.ok) throw new ApiClientError(response.status, "AB-5001");
  return await response.json() as StatusView;
}

export function writeStatusSnapshot(storage: Storage, value: StatusView, now = Date.now()): void {
  storage.setItem("arabom:status-snapshot", JSON.stringify({ savedAt: now, value }));
}

export function readStatusSnapshot(storage: Storage, now = Date.now()): StatusView | null {
  try {
    const item = JSON.parse(storage.getItem("arabom:status-snapshot") ?? "null") as { savedAt?: unknown; value?: unknown } | null;
    if (!item || typeof item.savedAt !== "number" || now - item.savedAt > SNAPSHOT_MAX_AGE_MS || !item.value) return null;
    return item.value as StatusView;
  } catch {
    return null;
  }
}

export async function fetchCaseList(mode: "missing" | "protected", fetcher: typeof fetch = fetch): Promise<CaseResponse> {
  const items: Array<ApiMissing | ApiProtected> = [];
  let stale = false;
  let cursor: string | null = null;
  let meta: ApiList<ApiMissing>["meta"] = { total: 0, checkedAt: null, nextCursor: null };
  const seenCursors = new Set<string>();
  do {
    const path = `/api/v1/${mode === "missing" ? "cases" : "protected"}?limit=100${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`;
    const response = await fetcher(resolveApiUrl(path), { headers: { accept: "application/json" }, cache: "no-store" });
    if (!response.ok) throw new Error(`case_list_${response.status}`);
    if(response.headers.get("X-Arabom-Snapshot")==="true")stale=true;
    const payload = await response.json() as ApiList<ApiMissing> | ApiList<ApiProtected>;
    items.push(...payload.items);
    meta = payload.meta;
    cursor = payload.meta.nextCursor ?? null;
    if (cursor && seenCursors.has(cursor)) throw new Error("case_list_cursor_loop");
    if (cursor) seenCursors.add(cursor);
  } while (cursor && items.length < 2_000);
  const payload = { meta: { ...meta, total: items.length, nextCursor: null }, items };
  const result = mode === "missing" ? adaptMissingResponse(payload as ApiList<ApiMissing>) : adaptProtectedResponse(payload as ApiList<ApiProtected>);
  return stale ? {...result,sourceStatus:"upstream_error",message:"네트워크 연결이 안 돼 24시간 이내 저장된 목록을 보여드려요."} : result;
}

export function writeCaseSnapshot(storage: Storage, mode: "missing" | "protected", data: CaseResponse, now = Date.now()): void {
  storage.setItem(`arabom:list-snapshot:${mode}`, JSON.stringify({ savedAt: now, data }));
}

export function readCaseSnapshot(storage: Storage, mode: "missing" | "protected", now = Date.now()): CaseResponse | null {
  try {
    const value = JSON.parse(storage.getItem(`arabom:list-snapshot:${mode}`) ?? "null") as { savedAt?: unknown; data?: unknown } | null;
    if (!value || typeof value.savedAt !== "number" || now - value.savedAt > 24 * 60 * 60 * 1000 || !value.data) return null;
    return value.data as CaseResponse;
  } catch {
    return null;
  }
}
